/**
 * Customer Locations Tab
 *
 * Displays and manages retail partner locations from the Customer Detail page.
 * Allows admin to:
 * - Enable multi-location support for a retail partner
 * - Add, edit, and delete store locations
 * - View Bridge Club member counts per location
 */

import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../services/api';
import './CustomerLocationsTab.css';

interface Location {
  id: number;
  name: string;
  short_name: string;
  full_display_name: string;
  address: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  zip_code: string;
  phone: string;
  email: string;
  is_active: boolean;
  is_primary: boolean;
  bridge_club_enabled: boolean;
  bridge_club_member_count: number;
  member_count?: number;
  store_code?: string;
  qr_urls?: {
    floor: string;
    register: string;
    shelf: string;
    general: string;
  };
}

interface RetailOrg {
  id: number;
  name: string;
  slug: string;
  logo: string | null;
  bridge_club_enabled: boolean;
  location_count: number;
  total_members: number;
}

interface Props {
  customerId: number;
  customerName: string;
}

const CustomerLocationsTab: React.FC<Props> = ({ customerId, customerName }) => {
  const [loading, setLoading] = useState(true);
  const [hasOrg, setHasOrg] = useState(false);
  const [org, setOrg] = useState<RetailOrg | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [enabling, setEnabling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);

  const copyQrUrl = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedUrl(label);
      setTimeout(() => setCopiedUrl(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  useEffect(() => {
    loadData();
  }, [customerId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(
        `${API_BASE_URL}/admin/customers/${customerId}/retail-org/`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        throw new Error('Failed to load retail org data');
      }

      const data = await response.json();
      setHasOrg(data.has_org);
      setOrg(data.org);
      setLocations(data.locations || []);
    } catch (err) {
      console.error('Failed to load retail org data:', err);
      setError('Failed to load location data');
    } finally {
      setLoading(false);
    }
  };

  const enableMultiLocation = async () => {
    try {
      setEnabling(true);
      setError(null);
      const response = await fetch(
        `${API_BASE_URL}/admin/customers/${customerId}/enable-multi-location/`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to enable multi-location');
      }

      await loadData();
    } catch (err: any) {
      console.error('Failed to enable multi-location:', err);
      setError(err.message || 'Failed to enable multi-location');
    } finally {
      setEnabling(false);
    }
  };

  const handleAddLocation = async (data: Partial<Location>) => {
    try {
      setError(null);
      const response = await fetch(
        `${API_BASE_URL}/admin/customers/${customerId}/locations/`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add location');
      }

      setShowAddModal(false);
      await loadData();
    } catch (err: any) {
      console.error('Failed to add location:', err);
      alert(err.message || 'Failed to add location');
    }
  };

  const handleUpdateLocation = async (locationId: number, data: Partial<Location>) => {
    try {
      setError(null);
      const response = await fetch(
        `${API_BASE_URL}/admin/locations/${locationId}/`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update location');
      }

      setEditingLocation(null);
      await loadData();
    } catch (err: any) {
      console.error('Failed to update location:', err);
      alert(err.message || 'Failed to update location');
    }
  };

  const handleDeleteLocation = async (location: Location) => {
    const memberCount = location.bridge_club_member_count || location.member_count || 0;
    if (memberCount > 0) {
      alert(`Cannot delete - ${memberCount} Bridge Club members are linked to this location`);
      return;
    }

    if (!window.confirm(`Delete location "${location.name}"? This cannot be undone.`)) {
      return;
    }

    try {
      setError(null);
      const response = await fetch(
        `${API_BASE_URL}/admin/locations/${location.id}/`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete location');
      }

      await loadData();
    } catch (err: any) {
      console.error('Failed to delete location:', err);
      alert(err.message || 'Failed to delete location');
    }
  };

  const handleSetPrimary = async (locationId: number) => {
    try {
      setError(null);
      const response = await fetch(
        `${API_BASE_URL}/admin/locations/${locationId}/set-primary/`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to set primary location');
      }

      await loadData();
    } catch (err: any) {
      console.error('Failed to set primary location:', err);
      alert(err.message || 'Failed to set primary location');
    }
  };

  if (loading) {
    return (
      <div className="locations-tab">
        <div className="loading-state">Loading locations...</div>
      </div>
    );
  }

  // Not set up for multi-location yet
  if (!hasOrg) {
    return (
      <div className="locations-tab">
        <div className="detail-section">
          <h3>Store Locations</h3>
          <div className="enable-multi-location-card">
            <div className="enable-icon">🏢</div>
            <p className="enable-description">
              This retail partner hasn't been set up for multiple locations yet.
            </p>
            {error && <div className="error-message">{error}</div>}
            <button
              onClick={enableMultiLocation}
              disabled={enabling}
              className="btn btn-primary"
            >
              {enabling ? 'Setting up...' : 'Enable Multi-Location'}
            </button>
            <div className="enable-benefits">
              <p>This will allow you to:</p>
              <ul>
                <li>Add multiple store locations</li>
                <li>Track Bridge Club members by location</li>
                <li>View analytics per location</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Has org - show locations
  return (
    <div className="locations-tab">
      <div className="detail-section">
        <div className="section-header">
          <h3>Store Locations</h3>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn btn-primary btn-sm"
          >
            + Add Location
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {org && (
          <div className="org-summary">
            <span className="org-name">{org.name}</span>
            <span className="org-stats">
              {org.location_count} location{org.location_count !== 1 ? 's' : ''} •{' '}
              {org.total_members} Bridge Club member{org.total_members !== 1 ? 's' : ''}
            </span>
          </div>
        )}

        <div className="locations-list">
          {locations.map((location) => (
            <div key={location.id} className={`location-card ${!location.is_active ? 'inactive' : ''}`}>
              <div className="location-header">
                <div className="location-info">
                  <div className="location-name">
                    <span className="location-icon">📍</span>
                    {location.name}
                    {location.is_primary && (
                      <span className="primary-badge">Primary</span>
                    )}
                    {!location.is_active && (
                      <span className="inactive-badge">Inactive</span>
                    )}
                  </div>
                  <div className="location-address">
                    {location.address_line1}
                    {location.address_line2 && `, ${location.address_line2}`}
                  </div>
                  <div className="location-city">
                    {location.city}, {location.state} {location.zip_code}
                  </div>
                  {location.phone && (
                    <div className="location-phone">{location.phone}</div>
                  )}
                  <div className="location-members">
                    Bridge Club Members: {location.bridge_club_member_count || location.member_count || 0}
                  </div>
                  {location.store_code && location.qr_urls && (
                    <div className="location-qr-urls">
                      <div className="qr-urls-header">
                        <span className="qr-icon">📱</span>
                        <span>QR URLs</span>
                        <span className="store-code-badge">{location.store_code}</span>
                      </div>
                      <div className="qr-urls-list">
                        {[
                          { key: 'floor', label: 'Floor', url: location.qr_urls.floor },
                          { key: 'register', label: 'Register', url: location.qr_urls.register },
                          { key: 'shelf', label: 'Shelf', url: location.qr_urls.shelf },
                        ].map(({ key, label, url }) => (
                          <div key={key} className="qr-url-row">
                            <span className="qr-placement">{label}:</span>
                            <code className="qr-url">{url}</code>
                            <button
                              className={`btn-copy ${copiedUrl === `${location.id}-${key}` ? 'copied' : ''}`}
                              onClick={() => copyQrUrl(url, `${location.id}-${key}`)}
                              title="Copy URL"
                            >
                              {copiedUrl === `${location.id}-${key}` ? '✓' : '📋'}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="location-actions">
                  {!location.is_primary && locations.length > 1 && (
                    <button
                      onClick={() => handleSetPrimary(location.id)}
                      className="btn-set-primary"
                      title="Set as primary location"
                    >
                      ⭐ Set Primary
                    </button>
                  )}
                  <button
                    onClick={() => setEditingLocation(location)}
                    className="btn-icon"
                    title="Edit location"
                  >
                    ✏️
                  </button>
                  {locations.length > 1 && !location.is_primary && (
                    <button
                      onClick={() => handleDeleteLocation(location)}
                      className="btn-icon btn-delete"
                      title="Delete location"
                    >
                      🗑️
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Staff Access Section */}
      <StaffAccessSection
        customerId={customerId}
        locations={locations}
      />

      {/* Location Analytics Section */}
      <LocationAnalyticsSection customerId={customerId} />

      {/* Add/Edit Location Modal */}
      {(showAddModal || editingLocation) && (
        <LocationModal
          location={editingLocation}
          onSave={(data) => {
            if (editingLocation) {
              handleUpdateLocation(editingLocation.id, data);
            } else {
              handleAddLocation(data);
            }
          }}
          onClose={() => {
            setShowAddModal(false);
            setEditingLocation(null);
          }}
        />
      )}
    </div>
  );
};

// Location Add/Edit Modal
interface LocationModalProps {
  location: Location | null;
  onSave: (data: Partial<Location>) => void;
  onClose: () => void;
}

const LocationModal: React.FC<LocationModalProps> = ({ location, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: location?.name || '',
    address_line1: location?.address_line1 || '',
    address_line2: location?.address_line2 || '',
    city: location?.city || '',
    state: location?.state || '',
    zip_code: location?.zip_code || '',
    phone: location?.phone || '',
    email: location?.email || '',
    is_active: location?.is_active ?? true,
  });

  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    await onSave(formData);
    setSaving(false);
  };

  // US States for dropdown
  const usStates = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content location-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{location ? 'Edit Location' : 'Add Location'}</h3>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-field full-width">
              <label>Location Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Spokane Valley, Downtown"
                required
              />
              <span className="field-hint">A short name to identify this location</span>
            </div>

            <div className="form-field full-width">
              <label>Street Address *</label>
              <input
                type="text"
                value={formData.address_line1}
                onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                placeholder="123 Main Street"
                required
              />
            </div>

            <div className="form-field full-width">
              <label>Address Line 2</label>
              <input
                type="text"
                value={formData.address_line2}
                onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
                placeholder="Suite 100"
              />
            </div>

            <div className="form-field">
              <label>City *</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                required
              />
            </div>

            <div className="form-field">
              <label>State *</label>
              <select
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                required
              >
                <option value="">Select State</option>
                {usStates.map((st) => (
                  <option key={st} value={st}>{st}</option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label>ZIP Code *</label>
              <input
                type="text"
                value={formData.zip_code}
                onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                placeholder="99999"
                required
              />
            </div>

            <div className="form-field">
              <label>Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="(509) 555-1234"
              />
            </div>

            <div className="form-field">
              <label>Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="store@example.com"
              />
            </div>

            {location && (
              <div className="form-field">
                <label>Status</label>
                <select
                  value={formData.is_active ? 'active' : 'inactive'}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'active' })}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            )}

            {location && location.store_code && (
              <div className="form-field full-width">
                <label>Store Code (for QR tracking)</label>
                <div className="store-code-display">
                  <code>{location.store_code}</code>
                  <span className="field-hint">Auto-generated. Contact support to change.</span>
                </div>
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : location ? 'Save Changes' : 'Add Location'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// =============================================================================
// STAFF ACCESS SECTION
// =============================================================================

interface PortalUser {
  id: number;
  user_id: number;
  name: string;
  email: string;
  role: string;
  is_org_owner: boolean;
  is_portal_active: boolean;
  assigned_locations: number[];
  assigned_location_names: string[];
}

interface StaffAccessSectionProps {
  customerId: number;
  locations: Location[];
}

const StaffAccessSection: React.FC<StaffAccessSectionProps> = ({ customerId, locations }) => {
  const [users, setUsers] = useState<PortalUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState<PortalUser | null>(null);

  useEffect(() => {
    loadUsers();
  }, [customerId]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/admin/customers/${customerId}/portal-users/`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (err) {
      console.error('Failed to load portal users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAccess = async (userId: number, data: {
    role?: string;
    is_org_owner?: boolean;
    location_ids?: number[];
  }) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/admin/portal-users/${userId}/location-access/`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update access');
      }

      setEditingUser(null);
      await loadUsers();
    } catch (err) {
      console.error('Failed to update access:', err);
      alert('Failed to update staff access');
    }
  };

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      owner: 'Owner',
      manager: 'Manager',
      staff: 'Staff',
      cashier: 'Cashier',
      viewer: 'Viewer',
    };
    return labels[role] || role;
  };

  const getAccessDisplay = (user: PortalUser) => {
    if (user.is_org_owner) {
      return 'All Locations';
    }
    if (user.assigned_location_names.length === 0) {
      return 'No locations assigned';
    }
    if (user.assigned_location_names.length === locations.length) {
      return 'All Locations';
    }
    return user.assigned_location_names.join(', ');
  };

  if (loading) {
    return (
      <div className="detail-section staff-section">
        <h3>Staff Access</h3>
        <div className="loading-state">Loading staff...</div>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="detail-section staff-section">
        <h3>Staff Access</h3>
        <div className="no-staff-message">
          No portal users found for this customer. Add portal users from the "Portal Access" tab.
        </div>
      </div>
    );
  }

  return (
    <div className="detail-section staff-section">
      <h3>Staff Access</h3>
      <div className="staff-list">
        {users.map((user) => (
          <div key={user.id} className={`staff-card ${!user.is_portal_active ? 'inactive' : ''}`}>
            <div className="staff-info">
              <div className="staff-header">
                <span className="staff-icon">👤</span>
                <span className="staff-name">{user.name}</span>
                <span className="staff-role">({getRoleLabel(user.role)})</span>
                {!user.is_portal_active && (
                  <span className="inactive-badge">Inactive</span>
                )}
              </div>
              <div className="staff-email">{user.email}</div>
              <div className="staff-access">
                Access: <span className={user.is_org_owner ? 'access-all' : ''}>
                  {getAccessDisplay(user)}
                </span>
              </div>
            </div>
            <div className="staff-actions">
              <button
                onClick={() => setEditingUser(user)}
                className="btn btn-sm btn-secondary"
              >
                Edit
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Staff Access Modal */}
      {editingUser && (
        <StaffAccessModal
          user={editingUser}
          locations={locations}
          onSave={(data) => handleUpdateAccess(editingUser.id, data)}
          onClose={() => setEditingUser(null)}
        />
      )}
    </div>
  );
};

// =============================================================================
// STAFF ACCESS MODAL
// =============================================================================

interface StaffAccessModalProps {
  user: PortalUser;
  locations: Location[];
  onSave: (data: { role: string; is_org_owner: boolean; location_ids: number[] }) => void;
  onClose: () => void;
}

const StaffAccessModal: React.FC<StaffAccessModalProps> = ({ user, locations, onSave, onClose }) => {
  const [role, setRole] = useState(user.role || 'staff');
  const [isOrgOwner, setIsOrgOwner] = useState(user.is_org_owner);
  const [selectedLocations, setSelectedLocations] = useState<number[]>(user.assigned_locations);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    await onSave({
      role,
      is_org_owner: isOrgOwner,
      location_ids: isOrgOwner ? [] : selectedLocations,
    });
    setSaving(false);
  };

  const toggleLocation = (locationId: number) => {
    setSelectedLocations((prev) =>
      prev.includes(locationId)
        ? prev.filter((id) => id !== locationId)
        : [...prev, locationId]
    );
  };

  const roles = [
    { value: 'owner', label: 'Owner' },
    { value: 'manager', label: 'Manager' },
    { value: 'staff', label: 'Staff' },
    { value: 'cashier', label: 'Cashier' },
    { value: 'viewer', label: 'Viewer' },
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content staff-access-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Edit Location Access - {user.name}</h3>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-field">
            <label>Role</label>
            <select value={role} onChange={(e) => setRole(e.target.value)}>
              {roles.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>

          <div className="form-field">
            <label>Location Access</label>
            <div className="location-checkboxes">
              {locations.map((loc) => (
                <label key={loc.id} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={isOrgOwner || selectedLocations.includes(loc.id)}
                    disabled={isOrgOwner}
                    onChange={() => toggleLocation(loc.id)}
                  />
                  <span>{loc.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-field">
            <label className="checkbox-label org-owner-checkbox">
              <input
                type="checkbox"
                checked={isOrgOwner}
                onChange={(e) => setIsOrgOwner(e.target.checked)}
              />
              <span>Access to ALL locations (org owner)</span>
            </label>
            <span className="field-hint">
              Org owners can see all locations and all analytics data
            </span>
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// =============================================================================
// LOCATION ANALYTICS SECTION
// =============================================================================

interface LocationAnalytics {
  location_id: number;
  location_name: string;
  store_code: string;
  total_visits: number;
  conversions: number;
  conversion_rate: number;
  placement_stats: Array<{ placement: string; count: number }>;
  daily_visits: Array<{ date: string; count: number }>;
}

interface LocationAnalyticsSectionProps {
  customerId: number;
}

const LocationAnalyticsSection: React.FC<LocationAnalyticsSectionProps> = ({ customerId }) => {
  const [analytics, setAnalytics] = useState<LocationAnalytics[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasData, setHasData] = useState(false);
  const [periodDays, setPeriodDays] = useState(30);

  useEffect(() => {
    loadAnalytics();
  }, [customerId, periodDays]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/admin/customers/${customerId}/location-analytics/?days=${periodDays}`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data.locations || []);
        setHasData(data.has_data);
      }
    } catch (err) {
      console.error('Failed to load analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const getPlacementLabel = (placement: string) => {
    const labels: Record<string, string> = {
      floor: 'Floor Sign',
      register: 'Cash Register',
      shelf: 'Shelf Display',
      bag: 'Product Bag',
      receipt: 'Receipt',
      email: 'Email',
      social: 'Social Media',
      website: 'Website',
      direct: 'Direct/Unknown',
      other: 'Other',
    };
    return labels[placement] || placement;
  };

  if (loading) {
    return (
      <div className="detail-section analytics-section">
        <h3>QR Visit Analytics</h3>
        <div className="loading-state">Loading analytics...</div>
      </div>
    );
  }

  if (analytics.length === 0) {
    return (
      <div className="detail-section analytics-section">
        <h3>QR Visit Analytics</h3>
        <div className="no-analytics-message">
          No locations with store codes found. Add store codes to locations to track QR visits.
        </div>
      </div>
    );
  }

  return (
    <div className="detail-section analytics-section">
      <div className="section-header">
        <h3>QR Visit Analytics</h3>
        <select
          value={periodDays}
          onChange={(e) => setPeriodDays(Number(e.target.value))}
          className="period-select"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      {!hasData ? (
        <div className="no-analytics-message">
          No visits recorded in the last {periodDays} days. Share QR codes to start tracking.
        </div>
      ) : (
        <div className="analytics-grid">
          {analytics.map((loc) => (
            <div key={loc.location_id} className="analytics-card">
              <div className="analytics-header">
                <span className="analytics-location-name">{loc.location_name}</span>
                <span className="analytics-store-code">{loc.store_code}</span>
              </div>

              <div className="analytics-stats">
                <div className="stat-item">
                  <span className="stat-value">{loc.total_visits}</span>
                  <span className="stat-label">Visits</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{loc.conversions}</span>
                  <span className="stat-label">Signups</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{loc.conversion_rate}%</span>
                  <span className="stat-label">Conversion</span>
                </div>
              </div>

              {loc.placement_stats.length > 0 && (
                <div className="placement-breakdown">
                  <div className="breakdown-title">By Placement</div>
                  {loc.placement_stats.slice(0, 4).map((ps) => (
                    <div key={ps.placement} className="breakdown-row">
                      <span className="breakdown-label">{getPlacementLabel(ps.placement)}</span>
                      <span className="breakdown-value">{ps.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomerLocationsTab;
